import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Alert,
  Stack,
  FormHelperText,
  CircularProgress,
} from '@mui/material';
import {
  accountCreateSchema,
  AccountCreateFormValues,
  ACCOUNT_REGIONS,
  ACCOUNT_LANGUAGES,
} from './accountCreateSchema';
import { useCreateAccount } from '../../../application/hooks/useAccounts';
import { useNotification } from '../../../../../shared/contexts/useNotification';

// ── Design tokens (Nexus Admin) ──────────────────────────────────────────────

const colors = {
  surface: '#f8f9ff',
  onSurface: '#0b1c30',
  onSurfaceVariant: '#434656',
  outline: '#737688',
  outlineVariant: '#c3c5d9',
  surfaceContainerLow: '#eff4ff',
  surfaceContainerLowest: '#ffffff',
  primary: '#003ec7',
  onPrimary: '#ffffff',
  error: '#ba1a1a',
  errorContainer: '#ffdad6',
};

const inputSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: colors.surfaceContainerLow,
    borderRadius: '8px',
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    overflow: 'hidden',
    '& fieldset': {
      borderColor: colors.outlineVariant,
      borderRadius: '8px',
    },
    '&:hover fieldset': {
      borderColor: colors.outlineVariant,
    },
    '&.Mui-focused fieldset': {
      borderColor: colors.primary,
      borderWidth: '1px',
      boxShadow: '0 0 0 3px rgba(0, 76, 237, 0.15)',
    },
    '&.Mui-error fieldset': {
      borderColor: colors.error,
    },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'Inter, sans-serif',
    fontSize: '14px',
    color: colors.outline,
    '&.Mui-focused': {
      color: colors.primary,
    },
    '&.Mui-error': {
      color: colors.error,
    },
  },
  '& .MuiFormHelperText-root': {
    fontFamily: 'Inter, sans-serif',
    fontSize: '12px',
    color: colors.outline,
    marginLeft: '4px',
  },
  '& .MuiFormHelperText-root.Mui-error': {
    color: colors.error,
  },
};

const outlinedButtonSx = {
  borderColor: colors.outlineVariant,
  color: colors.onSurfaceVariant,
  borderRadius: '8px',
  textTransform: 'none',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.05em',
  '&:hover': {
    borderColor: colors.outlineVariant,
    backgroundColor: 'rgba(115, 118, 136, 0.05)',
  },
};

const primaryButtonSx = {
  bgcolor: colors.primary,
  color: colors.onPrimary,
  borderRadius: '8px',
  textTransform: 'none',
  fontFamily: 'Inter, sans-serif',
  fontSize: '13px',
  fontWeight: 600,
  letterSpacing: '0.05em',
  '&:hover': {
    bgcolor: 'rgba(0, 62, 199, 0.9)',
  },
};

const selectSx = {
  backgroundColor: colors.surfaceContainerLow,
  borderRadius: '8px',
  fontFamily: 'Inter, sans-serif',
  fontSize: '14px',
  '& fieldset': {
    borderColor: colors.outlineVariant,
    borderRadius: '8px',
  },
  '&:hover fieldset': {
    borderColor: colors.outlineVariant,
  },
  '&.Mui-focused fieldset': {
    borderColor: colors.primary,
    borderWidth: '1px',
    boxShadow: '0 0 0 3px rgba(0, 76, 237, 0.15)',
  },
};

const REGION_LABELS: Record<(typeof ACCOUNT_REGIONS)[number], string> = {
  ES: 'Spain (ES)',
  'ES-CN': 'Canary Islands (ES-CN)',
  'ES-CT': 'Catalunya (ES-CT)',
};

const LANGUAGE_LABELS: Record<(typeof ACCOUNT_LANGUAGES)[number], string> = {
  'es-ES': 'Español (España)',
  'ca-ES': 'Català (Espanya)',
};

// ── Page ─────────────────────────────────────────────────────────────────────

/**
 * CreateAccountPage
 * Route: /accounts/new
 * Form to manually register a new business account (POST /admin/accounts).
 */
export const CreateAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const createAccount = useCreateAccount();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<AccountCreateFormValues>({
    resolver: zodResolver(accountCreateSchema),
    defaultValues: {
      businessName: '',
      ownerFirstName: '',
      ownerLastName: '',
      email: '',
      region: 'ES',
      language: 'es-ES',
    },
  });

  const onSubmit = (values: AccountCreateFormValues) => {
    createAccount.mutate(values, {
      onSuccess: () => {
        showNotification('Account created successfully', 'success');
        navigate('/accounts');
      },
      onError: () => {
        // Mutation error is also surfaced via Alert below
      },
    });
  };

  return (
    <Box sx={{ p: 5, maxWidth: 1000 }}>
      <Typography
        component="h1"
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '32px',
          fontWeight: 700,
          lineHeight: '40px',
          letterSpacing: '-0.02em',
          color: colors.onSurface,
          mb: '8px',
        }}
      >
        New Account
      </Typography>
      <Typography
        sx={{
          fontFamily: 'Inter, sans-serif',
          fontSize: '14px',
          color: colors.outline,
          mb: '32px',
        }}
      >
        Manually onboard a new business account. An invitation email will be sent to the owner so
        they can finish registration.
      </Typography>

      <Box component="form" onSubmit={handleSubmit(onSubmit)} noValidate>
        <Stack spacing={5}>
          {/* Business */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: '24px',
            }}
          >
            <Controller
              name="businessName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  label="Business name"
                  fullWidth
                  required
                  error={!!errors.businessName}
                  helperText={errors.businessName?.message}
                  sx={inputSx}
                />
              )}
            />

            <Controller
              name="email"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  label="Owner email"
                  type="email"
                  fullWidth
                  required
                  error={!!errors.email}
                  helperText={errors.email?.message}
                  sx={inputSx}
                />
              )}
            />
          </Box>

          {/* Owner */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: '24px',
            }}
          >
            <Controller
              name="ownerFirstName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  label="Owner first name"
                  fullWidth
                  required
                  error={!!errors.ownerFirstName}
                  helperText={errors.ownerFirstName?.message}
                  sx={inputSx}
                />
              )}
            />

            <Controller
              name="ownerLastName"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  variant="outlined"
                  label="Owner last name"
                  fullWidth
                  required
                  error={!!errors.ownerLastName}
                  helperText={errors.ownerLastName?.message}
                  sx={inputSx}
                />
              )}
            />
          </Box>

          {/* Locale */}
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
              gap: '24px',
            }}
          >
            <Controller
              name="region"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" error={!!errors.region} required sx={inputSx}>
                  <InputLabel sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                    Region
                  </InputLabel>
                  <Select
                    {...field}
                    label="Region"
                    sx={selectSx}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '8px',
                          border: `1px solid ${colors.outlineVariant}`,
                          marginTop: '4px',
                        },
                      },
                    }}
                  >
                    {ACCOUNT_REGIONS.map((value) => (
                      <MenuItem
                        key={value}
                        value={value}
                        sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}
                      >
                        {REGION_LABELS[value]}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.region && (
                    <FormHelperText sx={{ color: colors.error }}>
                      {errors.region.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />

            <Controller
              name="language"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth size="small" error={!!errors.language} required sx={inputSx}>
                  <InputLabel sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}>
                    Language
                  </InputLabel>
                  <Select
                    {...field}
                    label="Language"
                    sx={selectSx}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          borderRadius: '8px',
                          border: `1px solid ${colors.outlineVariant}`,
                          marginTop: '4px',
                        },
                      },
                    }}
                  >
                    {ACCOUNT_LANGUAGES.map((value) => (
                      <MenuItem
                        key={value}
                        value={value}
                        sx={{ fontFamily: 'Inter, sans-serif', fontSize: '14px' }}
                      >
                        {LANGUAGE_LABELS[value]}
                      </MenuItem>
                    ))}
                  </Select>
                  {errors.language && (
                    <FormHelperText sx={{ color: colors.error }}>
                      {errors.language.message}
                    </FormHelperText>
                  )}
                </FormControl>
              )}
            />
          </Box>

          {/* Server error */}
          {createAccount.isError && (
            <Alert
              severity="error"
              sx={{
                bgcolor: colors.errorContainer,
                color: colors.error,
                borderRadius: '8px',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {createAccount.error instanceof Error
                ? createAccount.error.message
                : 'An unexpected error occurred while creating the account'}
            </Alert>
          )}
        </Stack>

        {/* Actions */}
        <Stack
          direction="row"
          spacing={2}
          sx={{
            mt: 5,
            pt: 5,
            borderTop: '1px solid',
            borderColor: colors.outlineVariant,
            justifyContent: 'flex-end',
          }}
        >
          <Button
            variant="outlined"
            onClick={() => navigate('/accounts')}
            disabled={createAccount.isPending}
            sx={outlinedButtonSx}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={createAccount.isPending}
            startIcon={
              createAccount.isPending ? (
                <CircularProgress size={16} sx={{ color: colors.onPrimary }} />
              ) : undefined
            }
            sx={primaryButtonSx}
          >
            {createAccount.isPending ? 'Creating…' : 'Create Account'}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
};
